// Crash-safe in-progress order. During a (possibly split) payment, real card charges accumulate
// before the order is finalized/saved — if the browser dies mid-split, those charges would orphan.
// So we persist the WHOLE in-progress order (cart + every applied tender) to IndexedDB after each
// tender. On the next register load it can be RESUMED (the cashier finishes it) — full recovery, not
// just orphan detection. Reuses the offlineOrders table with status "in_progress" (the flush worker
// skips it; only "paid" records are sent to the server).

import { db } from "./db";
import { auth } from "./api";
import { snapshotCart } from "./heldOrders";

export const IN_PROGRESS = "in_progress";
const STALE_MS = 2 * 24 * 60 * 60 * 1000; // drop abandoned in-progress orders after 2 days

// Snapshot the full cart INCLUDING applied payment tenders (heldOrders' snapshot omits payments).
function fullSnapshot(cart) {
  const base = snapshotCart(cart); // deep clone of pre-payment fields
  base.payments = JSON.parse(JSON.stringify(cart.payments || []));
  base.activeOrderId = cart.activeOrderId || null; // restored by loadCart on resume → same order id
  base.totalsGrand = cart.totals ? cart.totals.grandTotal : 0;
  return base;
}

export async function saveActiveOrder(orderId, cart) {
  if (!orderId) return;
  try {
    const existing = await db.offlineOrders.get(String(orderId));
    await db.offlineOrders.put({
      orderId: String(orderId),
      status: IN_PROGRESS,
      merchantId: auth().merchantId || "", // scope recovery to the store the order belongs to
      snapshot: fullSnapshot(cart),
      payload: null,
      kind: "in_progress",
      createdAt: (existing && existing.createdAt) || Date.now(),
      updatedAt: Date.now(),
      attempts: 0,
      lastError: "",
    });
  } catch {
    /* IDB unavailable — non-fatal (Redux still holds the live order) */
  }
}

export async function clearActiveOrder(orderId) {
  if (!orderId) return;
  try {
    const o = await db.offlineOrders.get(String(orderId));
    if (o && o.status === IN_PROGRESS) await db.offlineOrders.delete(String(orderId));
  } catch {
    /* ignore */
  }
}

export async function listActiveOrders() {
  try {
    const rows = await db.offlineOrders.where("status").equals(IN_PROGRESS).toArray();
    // MERCHANT SCOPE: after a store switch, another store's mid-payment order must NOT surface here —
    // resuming it would finalize under the ACTIVE store's merchant_id (auth() at submit time) and
    // save the order to the wrong store. It resurfaces when the cashier switches back. Legacy rows
    // written before merchant stamping (no merchantId) are kept visible.
    const current = auth().merchantId || "";
    const mine = rows.filter((o) => !o.merchantId || o.merchantId === current);
    // prune abandoned ones
    const fresh = [];
    for (const o of mine) {
      if (Date.now() - (o.updatedAt || o.createdAt || 0) > STALE_MS) {
        await db.offlineOrders.delete(o.orderId).catch(() => {});
      } else {
        fresh.push(o);
      }
    }
    return fresh.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  } catch {
    return [];
  }
}

// Does an in-progress order carry a real (non-cash) charge that must be recovered/reconciled?
export function activeOrderHasCharges(o) {
  const p = o && o.snapshot && o.snapshot.payments;
  return Array.isArray(p) && p.some((x) => String(x.payType).toLowerCase() !== "cash" && Number(x.amount) > 0);
}
